import {
  createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import type { LiberacaoMeuDinheiro, Papel, Profile } from '@core/types';
import { limparCachePessoal, trancar } from '../pessoal/api';
import { liberacoesMeuDinheiro } from './consultas';
import { supabase } from './supabase';

// Papel e permissões vêm do banco (profiles + RLS). O que este contexto faz é
// só ESCONDER o que o usuário não pode fazer — a proibição de verdade acontece
// no Postgres. Se alguém burlar a UI, a RLS nega do mesmo jeito.

// --- Expiração por inatividade -----------------------------------------------
// 30 minutos, e o número tem os dois lados pensados:
//
//  Piso (não pode ser menor): um vendedor passa 20 minutos atendendo sem tocar
//  na tela — procura a peça na prateleira, prova no carro, negocia. Deslogar
//  no meio disso faz a equipe odiar o sistema, e sistema odiado volta para o
//  papel, que é o risco nº 1 declarado do projeto.
//
//  Teto (não pode ser maior): o celular do caixa fica destravado o dia inteiro
//  e o dono abre a folha nele. Sem expiração, qualquer um que pegue o aparelho
//  É o dono: salário da equipe, custo das peças, resultado do mês, e ainda
//  pode se promover em /equipe.
//
// Os 2 minutos finais são de AVISO na tela, com botão de continuar: ninguém
// perde uma venda pela metade sem ter tido chance de segurar a sessão.
export const INATIVIDADE_MS = 30 * 60_000;
export const AVISO_INATIVIDADE_MS = 2 * 60_000;

export type EstadoInatividade = 'ativo' | 'aviso' | 'expirado';

/** Em que ponto da contagem estamos, dado o tempo parado em ms. */
export function estadoDeInatividade(paradoMs: number): EstadoInatividade {
  if (paradoMs >= INATIVIDADE_MS) return 'expirado';
  if (paradoMs >= INATIVIDADE_MS - AVISO_INATIVIDADE_MS) return 'aviso';
  return 'ativo';
}

/**
 * Há quanto tempo esta SESSÃO está parada, dados todos os marcos conhecidos de
 * "aconteceu alguma coisa": a última interação nesta aba, o início da sessão e
 * a última atividade vista por QUALQUER aba deste navegador. Vale o mais
 * recente — parado é estar parado em todos eles.
 *
 * Cada marco entrou por um bug real, no mesmo dia (10/08/2026):
 *
 *  - **início da sessão**: quem acabou de entrar não tocou em nada AINDA, e o
 *    único relógio disponível era o do carregamento da página. No celular do
 *    balcão a tela de login fica aberta por horas, então logar significava ser
 *    expulso 15 segundos depois. Digitar a senha não ajudava: os ouvintes de
 *    teclado só passam a existir DEPOIS que há usuário.
 *
 *  - **atividade compartilhada**: as abas dividem o localStorage, logo dividem
 *    a SESSÃO — mas cada uma tinha seu próprio relógio de inatividade, e
 *    ouvinte de clique é por janela. A aba esquecida aberta atrás não via o
 *    vendedor trabalhando na da frente, se dava por expirada e derrubava a
 *    sessão das duas. Sessão compartilhada exige relógio compartilhado.
 */
export function paradoDesde(agora: number, ...marcos: Array<number | null | undefined>): number {
  const validos = marcos.filter((m): m is number => typeof m === 'number' && Number.isFinite(m));
  if (validos.length === 0) return 0;
  return agora - Math.max(...validos);
}

// Onde as abas deste navegador combinam "alguém está trabalhando". Fica fora do
// storageKey do supabase de propósito: apagar a sessão não pode apagar isto, e
// vice-versa.
const CHAVE_ATIVIDADE = 'serginho-ultima-atividade';

/** A última atividade vista por qualquer aba, ou null se não houver/der erro. */
export function lerAtividadeCompartilhada(): number | null {
  try {
    const bruto = window.localStorage.getItem(CHAVE_ATIVIDADE);
    if (bruto === null) return null;
    const n = Number(bruto);
    return Number.isFinite(n) ? n : null;
  } catch {
    // Modo privado, storage bloqueado por política: seguir com o relógio local.
    return null;
  }
}

export function gravarAtividadeCompartilhada(agora: number): void {
  try {
    window.localStorage.setItem(CHAVE_ATIVIDADE, String(agora));
  } catch {
    // Cota cheia ou storage negado. Perder o compartilhamento degrada para o
    // comportamento de uma aba só — nunca para deslogar alguém.
  }
}

/**
 * Gravar em disco a cada `pointerdown` seria escrever dezenas de vezes por
 * venda. Um carimbo a cada 5 s é resolução de sobra para um prazo de 30 min.
 */
export const INTERVALO_GRAVACAO_MS = 5_000;

// --- Perfil: ler a resposta do banco -----------------------------------------

export interface RespostaDePerfil {
  data: Profile | null;
  error: { message?: string } | null;
}

/**
 * Separa "não existe perfil" de "não deu para perguntar". O `maybeSingle`
 * devolve data=null nos DOIS casos; só o `error` distingue. Ignorá-lo fez o
 * app mandar quem voltou da suspensão com token vencido "pedir acesso ao dono"
 * — orientação errada para um problema de rede/sessão (visto em 2026-08-05).
 */
export function resultadoDoPerfil(resposta: RespostaDePerfil): { perfil: Profile | null; erro: boolean } {
  if (resposta.error) return { perfil: null, erro: true };
  return { perfil: resposta.data ?? null, erro: false };
}

// --- Sair tranca o Meu dinheiro antes -----------------------------------------

/**
 * Quanto a saída espera o banco pessoal trancar a sessão. Curto de propósito:
 * sair do sistema não pode ficar refém de um servidor lento — e a trava se
 * fecha sozinha em 15 minutos de qualquer jeito (pessoal/sql/functions/trava.sql).
 */
export const PRAZO_TRANCAR_AO_SAIR_MS = 1_500;

/**
 * Espera `tarefa` por no máximo `ms`. Falhar ou estourar o prazo NÃO é erro
 * aqui: quem chama segue em frente. O pedido que estourou o prazo continua
 * viajando — se chegar depois, a trava fecha do mesmo jeito, porque o token
 * ainda vale no servidor mesmo depois da saída local.
 */
export async function esperarNoMaximo(
  tarefa: Promise<unknown>, ms: number,
): Promise<'feito' | 'falhou' | 'prazo'> {
  let relogio: ReturnType<typeof setTimeout> | undefined;
  const prazo = new Promise<'prazo'>((resolve) => { relogio = setTimeout(() => resolve('prazo'), ms); });
  try {
    return await Promise.race([
      tarefa.then(() => 'feito' as const, () => 'falhou' as const),
      prazo,
    ]);
  } finally {
    clearTimeout(relogio);
  }
}

/**
 * Manda o service worker jogar fora o cache de consultas.
 * O `queryClient.clear()` limpa a memória da aba; isto limpa o que ficou em
 * DISCO. Sem os dois, o próximo a logar no celular do balcão pode receber,
 * offline, a resposta que o servidor montou para o usuário anterior.
 */
function limparCacheDoAparelho(): void {
  if (typeof navigator === 'undefined') return;
  navigator.serviceWorker?.controller?.postMessage({ tipo: 'serginho:limpar-dados' });
}

interface Sessao {
  session: Session | null;
  perfil: Profile | null;
  /** true quando a CONSULTA do perfil falhou (rede/token) — não é "sem perfil". */
  perfilErro: boolean;
  papel: Papel | null;
  carregando: boolean;
  /** true nos últimos minutos antes do logout automático. */
  avisoInatividade: boolean;
  /** Instante (epoch ms) do logout automático — só durante o aviso. */
  expiraEm: number | null;
  /** "Continuar conectado": zera a contagem de inatividade. */
  adiarInatividade: () => void;
  sair: () => Promise<void>;
  recarregarPerfil: () => Promise<void>;
}

const Ctx = createContext<Sessao | null>(null);

export function SessaoProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [perfil, setPerfil] = useState<Profile | null>(null);
  const [perfilErro, setPerfilErro] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [avisoInatividade, setAvisoInatividade] = useState(false);
  const [expiraEm, setExpiraEm] = useState<number | null>(null);

  // Quem estava logado até agora. Em ref, e não no estado, porque o callback do
  // onAuthStateChange é registrado uma vez só: ler `session` de lá pegaria o
  // valor do primeiro render para sempre.
  const usuarioRef = useRef<string | null>(null);
  const ultimaInteracao = useRef(Date.now());

  const buscarPerfil = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    // Resposta atrasada de OUTRO usuário não escreve nada: numa troca de conta,
    // a consulta lenta de quem saiu pode chegar depois da de quem entrou e
    // sobrescrever o perfil com o papel errado (a armadilha da folha, de novo).
    if (usuarioRef.current !== userId) return;
    const resultado = resultadoDoPerfil({ data: data as Profile | null, error });
    // Em erro, um perfil já carregado continua valendo: derrubar a tela por
    // uma falha passageira no refresh atrapalharia um atendimento em andamento
    // — e a permissão que vale é a da RLS, não a desta cópia local.
    setPerfil((atual) => (resultado.erro ? atual : resultado.perfil));
    setPerfilErro(resultado.erro);
  };

  const esquecerDadosDoUsuario = useCallback(() => {
    queryClient.clear();
    limparCacheDoAparelho();
  }, [queryClient]);

  useEffect(() => {
    let vivo = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!vivo) return;
      usuarioRef.current = data.session?.user.id ?? null;
      setSession(data.session);
      if (data.session?.user) await buscarPerfil(data.session.user.id);
      if (vivo) setCarregando(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (_evento, nova) => {
      const anterior = usuarioRef.current;
      const agora = nova?.user?.id ?? null;
      // Limpa quando a PESSOA muda — logout, ou outra conta entrando sem
      // recarregar a página (o caso real do celular compartilhado). Não limpa
      // no TOKEN_REFRESHED, que chega de hora em hora com o mesmo usuário:
      // jogar o cache fora ali seria recarregar tudo no meio de um atendimento.
      if (anterior !== null && anterior !== agora) {
        esquecerDadosDoUsuario();
        // O perfil de quem saiu não sobrevive à troca: se a consulta do usuário
        // novo falhar, o "preserva em erro" do buscarPerfil manteria o papel do
        // ANTERIOR — permissões de outra pessoa na tela.
        setPerfil(null);
      }
      usuarioRef.current = agora;

      setSession(nova);
      if (nova?.user) await buscarPerfil(nova.user.id);
      else {
        setPerfil(null);
        setPerfilErro(false);
      }
    });

    return () => {
      vivo = false;
      sub.subscription.unsubscribe();
    };
  }, [esquecerDadosDoUsuario]);

  const sair = useCallback(async () => {
    // Meu dinheiro (ADR-008): tranca a sessão no banco pessoal ANTES de sair —
    // depois do signOut não há mais token para pedir. Melhor esforço e com
    // prazo curto: falhar aqui não pode prender ninguém logado. E o que era do
    // Meu dinheiro sai da memória antes do signOut, sem depender dele dar certo.
    await esperarNoMaximo(trancar(), PRAZO_TRANCAR_AO_SAIR_MS);
    limparCachePessoal(queryClient);
    // `scope: 'local'` encerra ESTE aparelho, e só ele. O padrão do supabase-js
    // é 'global', que revoga a sessão em todo lugar: o balcão expirando por
    // inatividade derrubava junto o celular do dono e o computador da gerência,
    // no meio do que estivessem fazendo. Sair de um aparelho nunca foi pedido
    // para sair de todos — e quando for (aparelho perdido), a saída é trocar a
    // senha na tela de Equipe, que aí sim invalida tudo.
    await supabase.auth.signOut({ scope: 'local' });
    // Também no onAuthStateChange, mas repetido aqui de propósito: se o evento
    // não chegar (rede caída no meio do signOut), o cache não pode sobreviver.
    esquecerDadosDoUsuario();
    setPerfil(null);
    setPerfilErro(false);
  }, [esquecerDadosDoUsuario, queryClient]);

  const adiarInatividade = useCallback(() => {
    const agora = Date.now();
    ultimaInteracao.current = agora;
    gravarAtividadeCompartilhada(agora);
    setAvisoInatividade(false);
    setExpiraEm(null);
  }, []);

  const usuarioId = session?.user.id ?? null;

  useEffect(() => {
    if (!usuarioId) {
      setAvisoInatividade(false);
      setExpiraEm(null);
      return;
    }

    let saindo = false;
    // Este efeito só roda quando PASSA A EXISTIR usuário (ou quando ele muda),
    // então este instante é o começo da sessão. Guardar aqui, e não num ref de
    // fora, é o que garante que a contagem nunca herde o relógio da tela de
    // login — ver `paradoDesde`.
    const inicioDaSessao = Date.now();
    // Entrar já conta como atividade para as OUTRAS abas: sem isto, uma aba
    // aberta atrás continuaria com o carimbo velho e derrubaria este login.
    gravarAtividadeCompartilhada(inicioDaSessao);
    let gravadoEm = inicioDaSessao;

    const registrarInteracao = () => {
      const agora = Date.now();
      ultimaInteracao.current = agora;
      if (agora - gravadoEm >= INTERVALO_GRAVACAO_MS) {
        gravadoEm = agora;
        gravarAtividadeCompartilhada(agora);
      }
      setAvisoInatividade(false);
      setExpiraEm(null);
    };

    const conferir = () => {
      if (saindo) return;
      const compartilhada = lerAtividadeCompartilhada();
      const base = Math.max(ultimaInteracao.current, inicioDaSessao, compartilhada ?? 0);
      const estado = estadoDeInatividade(
        paradoDesde(Date.now(), ultimaInteracao.current, inicioDaSessao, compartilhada),
      );
      if (estado === 'expirado') {
        saindo = true;
        void sair();
        return;
      }
      const emAviso = estado === 'aviso';
      setAvisoInatividade(emAviso);
      setExpiraEm(emAviso ? base + INATIVIDADE_MS : null);
    };

    // Conta pelo relógio de parede, não por um setTimeout de 30 min: com a tela
    // do celular apagada o navegador congela temporizador longo, e ele pode
    // simplesmente não disparar. Aqui a pergunta é sempre "quanto tempo faz que
    // alguém encostou" — e o visibilitychange refaz a conta na hora em que o
    // aparelho volta do bolso.
    const relogio = window.setInterval(conferir, 15_000);
    document.addEventListener('visibilitychange', conferir);
    window.addEventListener('pointerdown', registrarInteracao, { passive: true });
    window.addEventListener('keydown', registrarInteracao, { passive: true });

    return () => {
      window.clearInterval(relogio);
      document.removeEventListener('visibilitychange', conferir);
      window.removeEventListener('pointerdown', registrarInteracao);
      window.removeEventListener('keydown', registrarInteracao);
    };
  }, [usuarioId, sair]);

  const valor: Sessao = {
    session,
    perfil,
    perfilErro,
    papel: perfil?.ativo ? perfil.papel : null,
    carregando,
    avisoInatividade,
    expiraEm,
    adiarInatividade,
    sair,
    recarregarPerfil: async () => {
      if (session?.user) await buscarPerfil(session.user.id);
    },
  };

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSessao(): Sessao {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSessao precisa estar dentro de <SessaoProvider>');
  return ctx;
}

// --- Permissões (espelham a matriz do WEB_PLAN §2.4) -------------------------
// Cada predicado tem nome de CAPACIDADE, não de papel: quando a matriz mudar,
// muda aqui e no rls.sql, e nenhuma tela precisa saber de papel nenhum.
// A checagem que vale é sempre a do banco — estas só escondem o que não serve.

/** Vender é o piso: todo perfil ativo vende. */
export const podeVender = (p: Papel | null) => p !== null;

/** Cadastrar peça, dar entrada de mercadoria, ajustar estoque. */
export const podeGerenciarCatalogo = (p: Papel | null) => p === 'gerente' || p === 'dono';

/** Cadastrar peça PELO BALCÃO — o just-in-time de 2026-08-06: peça antiga sem
 *  cadastro é cadastrada no ato da venda, por quem estiver no balcão. Todo
 *  papel ativo cria. Espelha a guarda de cadastrar_peca_pelo_balcao()
 *  (conferencia.sql): desde 08/10/2026 a peça do VENDEDOR nasce aguardando
 *  liberação e não vende até a gerência liberar; a do gerente e a do dono
 *  nascem liberadas. Editar, importar e dar entrada seguem na régua de
 *  podeGerenciarCatalogo. */
export const podeCadastroRapido = (p: Papel | null) => p !== null;

/** Liberar ou recusar a peça que o vendedor cadastrou (fila de conferência,
 *  093). Só gerente e dono — decisão do dono de 08/10/2026, e a mesma guarda
 *  de habilitar_peca() e recusar_peca() no banco. Capacidade separada de
 *  podeGerenciarCatalogo de propósito, mesmo com o mesmo corpo hoje: uma é
 *  "mexer no catálogo", a outra é "assinar o cadastro de outra pessoa". */
export const podeConferirCadastro = (p: Papel | null) => p === 'gerente' || p === 'dono';

/** Cobrar fiado, cancelar venda pelo código, devolver/trocar, emitir vale.
 *  Continua gerente+dono: é operação de balcão, não número da loja. */
export const podeGerenciarVendas = (p: Papel | null) => p === 'gerente' || p === 'dono';

/**
 * FATURAMENTO E QUANTO SE VENDE — só os donos (28/09/2026).
 *
 * O pai do dono pediu e o dono decidiu: funcionário não vê faturamento nem
 * quanto se vende. "Funcionário" aqui é VENDEDOR **e** GERENTE — o gerente é
 * da equipe como qualquer outro.
 *
 * Morre o AGREGADO: total do dia/mês/período, quantidade somada em dinheiro,
 * ticket médio, ranking, "sua contribuição", meta e comissão em dinheiro. FICA
 * o que é da venda em si: o valor de CADA uma, o subtotal do item e o recibo
 * que se entrega ao cliente.
 *
 * QUAL LISTA CADA UM VÊ é outra pergunta, e desde 07/10/2026 tem outro
 * predicado: `podeVerVendasDeTodos` logo abaixo. O vendedor vê as vendas dele;
 * o gerente passou a ver as de todo mundo — sem nenhuma soma.
 *
 * Capacidade separada de podeVerFinancas de propósito, mesmo com o mesmo corpo
 * hoje: uma é "o resultado e o custo da loja", a outra é "quanto entrou de
 * venda". Quem nega é o banco; esconder aqui é para não oferecer tela que a
 * RLS vai recusar no meio de um atendimento.
 */
export const podeVerFaturamento = (p: Papel | null) => p === 'dono';

/**
 * A VISTA das vendas de todo mundo — gerente e dono (07/10/2026).
 *
 * O dono: "Gostaria que meu gerente pudesse ver todas as vendas (...). O
 * objetivo é que ele visualize o valor de CADA venda para documentar, mas não
 * gostaria que ele pudesse ver o VALOR TOTAL das vendas somadas (o que
 * 'faturamos' hoje)."
 *
 * É a LISTA, e só ela. Este predicado abre a lista de todos e o filtro por
 * vendedor; quem continua fechando os três cartões de soma é
 * `podeVerFaturamento` acima — e os dois têm de continuar SEPARADOS, porque é
 * exatamente a fronteira que o dono desenhou: ver cada venda, sim; ver a soma,
 * não. Trocar um pelo outro em Vendas.tsx devolve o faturamento ao gerente.
 *
 * O nome não leva "Faturamento" de propósito: as réguas do sino são conferidas
 * por um teste que lê o TEXTO do fonte procurando `podeVerFaturamento`
 * (Sino.reguas.test.ts), e um nome parecido casaria com a linha errada,
 * deixando aquele teste verde sem proteger nada.
 *
 * Quem nega é o banco: a policy de `sales` continua dando ao vendedor só as
 * vendas dele, e a lista da gerência entra por `vendas_para_gerencia()`, que é
 * definer com exige_papel no corpo.
 */
export const podeVerVendasDeTodos = (p: Papel | null) => p === 'gerente' || p === 'dono';

/**
 * O caixa da loja: abrir, fechar, sangria, conferir venda, relatório de caixa.
 * Era de gerente+dono até 28/09/2026 — e passou a ser só do dono junto com a
 * decisão acima, porque o caixa é o faturamento do dia contado em dinheiro.
 * A reposição, que o gerente continua usando, saiu de baixo desta régua e
 * ficou na de podeGerenciarCatalogo (é estoque, não dinheiro).
 */
export const podeVerCaixa = (p: Papel | null) => p === 'dono';

/** Folha de pagamento, custo das peças e resultado — só o dono. */
export const podeVerFinancas = (p: Papel | null) => p === 'dono';

/**
 * LANÇAR a compra paga na hora ao fornecedor — "Paguei fornecedor" (095,
 * 08/10/2026). Gerente e dono: o gerente cobre o "tem vezes que não estou".
 *
 * É ESCREVER sem ver. A lista e o total do mês das compras continuam só do
 * dono (`podeVerFinancas` dentro da tela): o gerente lança, com a data de
 * hoje, e só desfaz o que ele mesmo lançou nos primeiros 15 minutos. Quem nega
 * é o banco — exige_papel(['gerente','dono']) no corpo de
 * registrar_compra_na_hora. Voltar para "só o dono" é trocar esta linha e a
 * primeira da função.
 */
export const podeLancarCompraNaHora = (p: Papel | null) => p === 'gerente' || p === 'dono';

/**
 * Abrir a tela de Equipe e cadastrar funcionário.
 * Gerente entra aqui, mas só administra VENDEDOR — a hierarquia é aplicada na
 * RLS (rls.sql §6) e na Edge Function; aqui só escondemos o que ele não pode.
 */
export const podeGerenciarEquipe = (p: Papel | null) => p === 'gerente' || p === 'dono';

/** Define salário/comissão, muda papéis, exclui cadastro — só o dono. */
export const podeAdministrar = (p: Papel | null) => p === 'dono';

/**
 * Voltar o mapa da loja para uma versão anterior (084, pedido do dono em
 * 25/09/2026). Editar e salvar o mapa é de quem gerencia o catálogo (dono e
 * gerente); desfazer o histórico é só do dono. Espelha o exige_papel de
 * restaurar_planta_da_loja.
 */
export const podeVoltarOMapa = (p: Papel | null) => p === 'dono';

/**
 * Corrigir as DATAS de um documento já emitido — hoje, a encomenda: o prazo
 * andando nos dois sentidos e o dia em que ela foi aberta.
 *
 * Só o dono, e é capacidade separada de podeGerenciarVendas de propósito:
 * PRORROGAR (empurrar o prazo para frente) é operação de balcão e continua de
 * todo mundo — o cliente ligou pedindo mais prazo. Voltar a data encurta uma
 * promessa e reescreve o papel que o cliente tem na mão, e foi assim que o dono
 * pediu. Espelha a guarda de corrigir_datas_da_encomenda() em encomendas.sql;
 * quem nega de verdade é o banco.
 */
export const podeCorrigirDatas = (p: Papel | null) => p === 'dono';

/**
 * Trocar QUEM SEPAROU a encomenda (18/09/2026) — o dono lançou a encomenda que
 * um vendedor atendeu, e ela ficou presa ao nome dele. Só o dono, pela mesma
 * razão das datas: é conserto de documento, não operação de balcão. Espelha a
 * guarda de trocar_vendedor_da_encomenda() em encomendas.sql.
 */
export const podeTrocarVendedorDaEncomenda = (p: Papel | null) => p === 'dono';

/**
 * Passar vendas de uma pessoa da equipe para outra (01/10/2026) — "todas as
 * vendas do Alex de hoje passar pro nome do Renan". Só o dono: muda de quem é
 * a venda, a meta e a base da comissão. Espelha a guarda de
 * trocar_vendedor_das_vendas() em trocar_vendedor_da_venda.sql.
 */
export const podeTrocarVendedorDaVenda = (p: Papel | null) => p === 'dono';

/**
 * Reescrever o CONTEÚDO de uma venda — peças, preços, desconto (070). Só o
 * dono, e separado de podeGerenciarVendas de propósito: o gerente cancela e
 * corrige digitação, mas reescrever preço mexe na comissão e na meta de quem
 * vendeu. O banco nega igual (exige_papel no corpo da RPC); isto só evita
 * oferecer um botão que vai dar erro.
 */
export const podeCorrigirVenda = (p: Papel | null) => p === 'dono';

/** Quem este papel consegue administrar. Espelha rls.sql §6. */
export const podeAdministrarPapel = (meu: Papel | null, alvo: Papel): boolean =>
  meu === 'dono' || (meu === 'gerente' && alvo === 'vendedor');

// --- Meu dinheiro: quem vê o item no menu (migração 079) ----------------------
//
// O dono liga e desliga por PAPEL, na tela de Equipe. Aqui é só o espelho para
// o menu e para a rota: quem nega de verdade é o banco pessoal, que só atende
// quem está na lista liberada (ADR-008). Esconder o item de quem não pode é
// para não oferecer uma porta que o banco vai fechar.

/** A chave da consulta do botão — a MESMA no menu e na tela de Equipe, para o
 *  menu de quem está no aparelho mudar na hora em que o dono mexe. */
export const CHAVE_LIBERACOES_MEU_DINHEIRO = ['liberacoes-meu-dinheiro'] as const;

/** Este papel pode usar o Meu dinheiro? Sem papel (sessão caindo), nunca. */
export function meuDinheiroLiberadoPara(
  papel: Papel | null, liberacoes: readonly LiberacaoMeuDinheiro[] | undefined,
): boolean {
  if (papel === null || !liberacoes) return false;
  return liberacoes.some((l) => l.papel === papel && l.liberado);
}

/**
 * Liberado e AINDA CARREGANDO, separados: a rota precisa distinguir "não pode"
 * de "ainda não sei" — senão quem abre /pessoal/meu-dinheiro direto vê meio segundo de
 * "Acesso restrito" antes de a lista chegar.
 */
export function useLiberacaoMeuDinheiro(): { liberado: boolean; carregando: boolean } {
  const { papel } = useSessao();
  const consulta = useQuery({
    queryKey: CHAVE_LIBERACOES_MEU_DINHEIRO,
    queryFn: liberacoesMeuDinheiro,
    enabled: papel !== null,
    // Um minuto: o menu é remontado a cada troca de tela, e é assim que o botão
    // do dono chega ao aparelho dos outros sem recarregar a página.
    staleTime: 60_000,
  });
  return {
    liberado: meuDinheiroLiberadoPara(papel, consulta.data),
    carregando: papel !== null && consulta.isLoading,
  };
}

/** O papel de quem está logado pode usar o Meu dinheiro? */
export function useMeuDinheiroLiberado(): boolean {
  return useLiberacaoMeuDinheiro().liberado;
}
